/**
 * Error Boundary Component
 * Global error handler that catches React rendering errors
 * Displays a friendly error message with reload button
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-bg-base flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            {/* Error icon */}
            <div className="mb-6">
              <svg
                className="w-20 h-20 mx-auto text-status-abrogated"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Message */}
            <h1 className="text-2xl font-bold text-text-primary mb-3">
              Ceva nu a mers bine
            </h1>
            <p className="text-text-secondary mb-8">
              A apărut o eroare neașteptată. Vă rugăm să reîncărcați pagina
              sau să navigați către pagina principală.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-3 bg-accent text-bg-base font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Reîncarcă pagina
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-6 py-3 bg-surface text-text-primary font-semibold rounded-lg border border-border hover:bg-bg-hover transition-colors"
              >
                Mergi la pagina principală
              </button>
            </div>

            {/* Error details (development only) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-sm text-text-tertiary cursor-pointer hover:text-text-secondary">
                  Detalii eroare (doar development)
                </summary>
                <pre className="mt-2 p-4 bg-bg-surface rounded-lg text-xs text-status-abrogated overflow-auto max-h-48">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
